
import {Component, Vue} from "vue-property-decorator";
import {Form, Message, Tree} from "element-ui";
import {cloneDeep} from "lodash";
import {getJobPositionList} from "@/api/job-position";
import {createUser, defaultUserData, deleteUser, getUsers, updateUser,} from "@/api/users";
import {IJobPositionModel, IMenuModel, IRoleModel, IUserModel,} from "@/api/types";
import Pagination from "@/components/Pagination/index.vue";
import {createUserPermission, defaultRoleData, getRoles, getUserPermissionDetail,} from "@/api/role";
import {getMenus} from "@/api/resource";

@Component({
  name: "UserManagement",
  components: {
    Pagination,
  },
  filters: {},
})
export default class extends Vue {
  private tableKey = 0;
  private userList: IUserModel[] = [];
  private jobPositionList: IJobPositionModel[] = [];
  private roleList: IRoleModel[] = [];
  private total = 0;
  private dataLoading = false;
  private listQuery = {
    pageNumber: 1,
    pageSize: 20,
    keyword: "",
    state: null,
  };

  private defaultPassword = "******";
  private statusOptions = { 1: "在职", 2: "离职" };
  private viewData = { 1: "普通员工", 2: "经理" };
  private roleIdAndNameMap: { [key: string]: string } = {};
  private jobPositionIdAndNameMap: { [key: string]: string } = {};

  private dialogFormVisible = false;
  private dialogPermissionVisible = false;
  private resources: IMenuModel[] = [];
  private tempRoleData = cloneDeep(defaultRoleData);
  private selectedPermissionCount = 0;
  private totalPermissionCount = 0;

  private dialogStatus = "";
  private textMap = {
    update: "编辑用户",
    create: "新增用户",
    updateRole: "编辑权限"
  };

  get permissionPercent() {
    if (!this.totalPermissionCount) {
      return 0;
    }
    return Math.min(
      100,
      Math.round((this.selectedPermissionCount / this.totalPermissionCount) * 100)
    );
  }

  private rules = {
    name: [{ required: true, message: "姓名不能为空", trigger: "blur" }],
    telephone: [{ required: true, message: "手机号不能为空", trigger: "blur" }],
    staffCode: [
      { required: true, message: "员工编号不能为空", trigger: "blur" },
    ],
    jobPositionId: [
      { required: true, message: "岗位不能为空", trigger: "blur" },
    ],
    roleId: [{ required: true, message: "角色不能为空", trigger: "blur" }],
    password: [{ required: true, message: "密码不能为空", trigger: "blur" }],
    state: [{ required: true, message: "状态不能为空", trigger: "blur" }]
  };

  private tempUserData = defaultUserData;

  private selectAccount:string = null;

  created() {
    this.initJobPositions();
    this.initRoles();
    this.getList();
  }

  private async initJobPositions() {
    const data: any = await getJobPositionList();
    this.jobPositionList = data;
    this.jobPositionIdAndNameMap = this.jobPositionList.reduce(
      (acc: { [key: string]: string }, cur) => {
        acc[cur.id] = cur.name;
        return acc;
      },
      {}
    ) as { [key: string]: string };
  }

  private async initRoles() {
    const data: any = await getRoles();
    this.roleList = data;
    this.roleIdAndNameMap = this.roleList.reduce(
      (acc: { [key: string]: string }, cur) => {
        acc[cur.id] = cur.name;
        return acc;
      },
      {}
    ) as { [key: string]: string };
  }

  private async getList() {
    this.dataLoading = true;
    const { total, content }: any = await getUsers(this.listQuery);
    this.total = total;
    this.userList = content;
    setTimeout(() => {
      this.dataLoading = false;
    }, 0.5 * 1000);
  }

  private async getResources() {
    const data: any = await getMenus(this.selectAccount);
    this.resources = data;
    this.totalPermissionCount = this.countPermissionNodes(data);
  }

  private countPermissionNodes(nodes: IMenuModel[]): number {
    if (!nodes || !nodes.length) {
      return 0;
    }
    return nodes.reduce((count, node) => {
      const children = node.children || [];
      if (!children.length) {
        return count + 1;
      }
      return count + this.countPermissionNodes(children);
    }, 0);
  }

  private updatePermissionCount() {
    const tree = this.$refs.menuTree as Tree;
    if (!tree) {
      this.selectedPermissionCount = 0;
      return;
    }
    const checkedLeafIds = tree.getCheckedKeys(true) as string[];
    this.selectedPermissionCount = checkedLeafIds.length;
  }

  private handleFilter() {
    this.listQuery.pageNumber = 1;
    this.getList();
  }

  private resetTempArticleData() {
    this.tempUserData = cloneDeep(defaultUserData);
  }

  private handleCreate(account:string = null) {
    this.resetTempArticleData();
    this.selectAccount = account;
    this.dialogStatus = "create";
    this.dialogFormVisible = true;
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate();
    });
  }

  private createData() {
    (this.$refs.dataForm as Form).validate(async (valid) => {
      if (valid) {
        const userData = this.tempUserData;
        await createUser({...userData, accountType: this.selectAccount});
        this.dialogFormVisible = false;
        this.$notify({
          title: "成功",
          message: "创建成功",
          type: "success",
          duration: 2000,
        });
        await this.getList();
      }
    });
  }

  private async onUpdatePermission(row: any, account = null) {
    this.tempRoleData = Object.assign({}, row);
    this.selectAccount = account;
    await this.getResources();
    const menus: any = await getUserPermissionDetail(row.id, account);
    this.tempRoleData.menus = menus;
    this.dialogStatus = "updateRole";
    this.dialogPermissionVisible = true;
    this.selectedPermissionCount = 0;
    this.$nextTick(() => {
      (this.$refs.menuTree as Tree).setCheckedKeys([]);
      menus.map((i) => {
        const node = (this.$refs.menuTree as Tree).getNode(i);
        if (node && node.isLeaf) {
          (this.$refs.menuTree as Tree).setChecked(node, true, true);
        }
      });
      this.updatePermissionCount();
    });
  }

  private async doUpdatePermission() {
    const userIds = this.tempRoleData.id;
    if (!userIds || userIds === "") {
      Message.error("请选择用户");
      return;
    }
    const checkedIds: string[] = (this.$refs.menuTree as Tree).getCheckedKeys(
      false
    );
    let halfChecked: string[] = (
      this.$refs.menuTree as Tree
    ).getHalfCheckedKeys();
    checkedIds.push(...halfChecked);
    await createUserPermission(userIds, checkedIds, this.selectAccount);
    this.dialogPermissionVisible = false;
    this.$notify({
      title: "成功",
      message: "权限已保存",
      type: "success",
      duration: 2000,
    });
    await this.getList();
  }

  private onUpdate(row: any, account:string = null) {
    this.tempUserData = Object.assign({}, row);
    this.selectAccount = account;
    this.tempUserData.password = this.defaultPassword;
    this.dialogStatus = "update";
    this.dialogFormVisible = true;
    this.$nextTick(() => {
      (this.$refs.dataForm as Form).clearValidate();
    });
  }

  private updateData() {
    (this.$refs.dataForm as Form).validate(async (valid) => {
      if (valid) {
        const params = Object.assign({}, this.tempUserData);
        if (params.password === this.defaultPassword) {
          params.password = null;
        }
        await updateUser(this.tempUserData.id, {...params, accountType: this.selectAccount});
        this.dialogFormVisible = false;
        this.$notify({
          title: "成功",
          message: "更新成功",
          type: "success",
          duration: 2000,
        });
      }
      await this.getList();
    });
  }

  private async onDelete(row: IUserModel) {
    await deleteUser(row.id);
    this.$notify({
      title: "Success",
      message: "删除成功",
      type: "success",
      duration: 2000,
    });
    await this.getList();
  }
}
